import { computed, effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, switchMap } from 'rxjs';

import { uniqueBy } from '@zeusm/personal/core/helpers';
import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@zeusm/shared/store';

import { DICTIONARIES_API } from './api';
import { DictionariesState } from './models';

const initialState: DictionariesState = {
  currencies: [],
  rates: [],
};

export const DictionariesStore = signalStore(
  withDevtools('dictionaries'),
  withState(initialState),
  withRequestStatus(['getCurrencies', 'getRates']),
  withRequestHandler(),
  withProps(store => ({
    store,
    dictionariesApi: inject(DICTIONARIES_API),
    storeHelperService: inject(StoreHelperService),
    spinnerService: inject(SpinnerService),
  })),
  withComputed(({ currencies, rates }) => ({
    fiatCurrencies: computed(() => {
      return currencies().filter(item => !item.isCrypto);
    }),
    cryptoCurrencies: computed(() => {
      return currencies().filter(item => item.isCrypto);
    }),
    rateIndex: computed(() => {
      return new Map(rates().map(({ from, to, rate }) => [`${from}-${to}`, rate]));
    }),
    currencyDictionary: computed(() => {
      const list = currencies().map(({ symbol }) => ({
        id: symbol,
        name: symbol,
      }));

      return uniqueBy(list, item => item.id);
    }),
  })),
  withMethods(store => ({
    clearStoreData: () => {
      patchState(store, initialState);
      store.resetRequestStatus('getCurrencies');
      store.resetRequestStatus('getRates');
    },

    getRate(from: string, to: string): string {
      if (!from || !to) return null;

      return store.rateIndex().get(`${from}-${to}`);
    },

    getCurrency(value: string, field: 'id' | 'symbol' = 'id') {
      return store.currencies().find(c => c[field] === value);
    },
  })),
  withMethods(({ store, dictionariesApi }) => ({
    getCurrencies: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getCurrencies'));
        return (store.currencies().length ? of(store.currencies()) : dictionariesApi.getCurrencies()).pipe(
          tapResponse({
            next: currencies => {
              patchState(store, { currencies }, setSuccessStatusFor('getCurrencies'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCurrencies', error)),
          }),
        );
      }),
    ),

    getRates: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getRates'));
        return (store.rates().length ? of(store.rates()) : dictionariesApi.getRates()).pipe(
          tapResponse({
            next: rates => {
              patchState(store, { rates }, setSuccessStatusFor('getRates'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getRates', error)),
          }),
        );
      }),
    ),
  })),
  withMethods(({ store, getRates }) => ({
    refreshRates: () => {
      patchState(store, { rates: [] });
      getRates();
    },
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));

      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
